import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { managedLocations } from '../../auth/managedLocations';
import { formatDateSafe, toDate } from '../../../shared/utils/dateHelpers';
import { useClientDirectory } from '../hooks/useClientDirectory';
import { ClientDirectoryClient, UpcomingAppointment } from '../services/clientDirectoryService';

const transformName = (name: string | null | undefined): string => {
  if (!name) return 'Unknown';
  if (name.includes(',')) {
    const [lastName, firstName] = name.split(',').map(part => part.trim());
    return `${firstName} ${lastName}`;
  }
  return name;
};

const getNextBooking = (
  appointments: UpcomingAppointment[]
): { date: Date; coach: string | undefined; location: string | undefined } | null => {
  const now = Date.now();
  type BookingCandidate = { date: Date; ts: number; coach: string | undefined; location: string | undefined };
  const upcoming = appointments
    .map((appointment): BookingCandidate | null => {
      const dateValue = appointment?.date?.value;
      const date = toDate(dateValue);
      if (!date) return null;
      return {
        date,
        ts: date.getTime(),
        coach: appointment.coach,
        location: appointment.location,
      };
    })
    .filter((value): value is BookingCandidate => value !== null)
    .filter(appointment => appointment.ts >= now)
    .sort((a, b) => a.ts - b.ts);

  if (upcoming.length === 0) return null;
  const nextBooking = upcoming[0];
  return { date: nextBooking.date, coach: nextBooking.coach, location: nextBooking.location };
};

export const ClientOmnibox: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { userRole, userMetadata } = useAuth();

  const [hasInteracted, setHasInteracted] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const { clients, loading, error } = useClientDirectory({ enabled: hasInteracted });

  const filteredOptions = useMemo(() => {
    const queryText = inputValue.trim().toLowerCase();
    if (!queryText) return [];

    const scored = clients
      .map(client => {
        const name = transformName(client.client_name).toLowerCase();
        const id = client.client_id.toLowerCase();
        const matches = name.includes(queryText) || id.includes(queryText);
        if (!matches) return null;

        const rank = id === queryText ? 0 : id.startsWith(queryText) ? 1 : name.startsWith(queryText) ? 2 : 3;
        return { client, rank, name };
      })
      .filter((value): value is { client: ClientDirectoryClient; rank: number; name: string } => Boolean(value))
      .sort((a, b) => a.rank - b.rank || a.name.localeCompare(b.name))
      .slice(0, 10)
      .map(value => value.client);

    return scored;
  }, [clients, inputValue]);

  const disabledReason = useMemo(() => {
    if (!userRole) return 'Loading access…';
    if (userRole === 'MANAGER' && managedLocations(userMetadata).length === 0) return 'No assigned location';
    if (userRole === 'USER' && !userMetadata?.coach_id) return 'No coach ID';
    return null;
  }, [userRole, userMetadata]);

  return (
    <Box sx={{ width: { xs: 200, sm: 280, md: 420 }, maxWidth: '100%' }}>
      <Autocomplete
        onOpen={() => setHasInteracted(true)}
        openOnFocus
        value={null}
        onChange={(_, value) => {
          if (value) {
            navigate(`/clients/${value.id}`);
          }
          setInputValue('');
        }}
        inputValue={inputValue}
        onInputChange={(_, value) => {
          setHasInteracted(true);
          setInputValue(value);
        }}
        options={filteredOptions}
        filterOptions={(options) => options}
        loading={loading}
        autoHighlight
        clearOnBlur={false}
        disablePortal={false}
        getOptionLabel={(option) => `${transformName(option.client_name)} (${option.client_id})`}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        noOptionsText={
          error
            ? error
            : inputValue.trim().length === 0
              ? 'Type to search clients'
              : 'No matching clients'
        }
        renderOption={(props, option) => {
          const upcomingCount = option.upcoming_appointments?.length ?? 0;
          const nextBooking = getNextBooking(option.upcoming_appointments ?? []);

          const upcomingText =
            upcomingCount === 0
              ? 'None'
              : nextBooking
                ? `${formatDateSafe(nextBooking.date, 'dd MMM yyyy')}${
                    nextBooking.location ? ` @ ${nextBooking.location}` : ''
                  } (${upcomingCount})`
                : `${upcomingCount}`;

          return (
            <Box component="li" {...props} key={option.id}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" noWrap>
                  {transformName(option.client_name)}{' '}
                  <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
                    #{option.client_id}
                  </Typography>
                </Typography>
                <Typography variant="caption" noWrap sx={{ color: 'text.secondary', display: 'block' }}>
                  Lead Coach: {transformName(option.lead_coach_name)}
                </Typography>
                <Typography variant="caption" noWrap sx={{ color: 'text.secondary', display: 'block' }}>
                  Last session:{' '}
                  {option.last_visit_ts ? formatDateSafe(new Date(option.last_visit_ts), 'dd MMM yyyy', 'N/A') : 'N/A'} • Upcoming: {upcomingText}
                </Typography>
              </Box>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder={disabledReason ? disabledReason : 'Search clients by name or ID…'}
            disabled={Boolean(disabledReason)}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: alpha('#ffffff', 0.08),
                borderRadius: 999,
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha('#ffffff', 0.18),
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha('#ffffff', 0.28),
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha('#ffffff', 0.4),
                },
              },
              '& .MuiInputBase-input::placeholder': {
                color: alpha('#ffffff', 0.7),
                opacity: 1,
              },
            }}
          />
        )}
        PaperComponent={(paperProps) => (
          <Box
            {...paperProps}
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              mt: 1,
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              boxShadow: '0 18px 60px rgba(0, 0, 0, 0.18)',
              overflow: 'hidden',
            }}
          />
        )}
      />
    </Box>
  );
};
